import React, { useCallback, useEffect, useMemo, useState } from 'react';
// The project's own pad, not react-signature-canvas: that component defaults to
// clearOnResize, so on an iPad a rotation or the on-screen keyboard silently
// wipes a signature someone has already drawn. SignaturePad uses a fixed canvas
// with scaled pointer coordinates and handles touch/stylus.
import SignaturePad from './SignaturePad.jsx';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import PdfPageStack from './PdfPageStack.jsx';
import { rectToStyle, fieldFontPx } from '../utils/pdfRender.js';
import { templatePdfUrl } from '../utils/pdfFormTemplate.js';
import './PdfFormFiller.css';

/**
 * PDF FORM FILLER (parent-facing)
 *
 * Shows the club's actual document with real inputs sitting on its blanks, so a
 * parent reads what they are signing and fills it in place — the way they would
 * with a pen at the front desk.
 *
 * Two layouts from one field map:
 *   overlay  — inputs positioned on the rendered page. Good on an iPad kiosk.
 *   list     — one field at a time beneath a page preview. Necessary on a phone,
 *              where a blank on a 612pt-wide page is about six pixels tall.
 *
 * Values that registration already knows arrive prefilled, so this is usually a
 * short review rather than twenty-odd fields of typing.
 */

const CONSENT_TEXT =
  'I agree that my electronic signature is the legal equivalent of my handwritten signature, ' +
  'and that I am the parent or legal guardian authorised to sign this form.';

const inputTypeFor = (type) =>
  ({ date: 'date', email: 'email', tel: 'tel', number: 'number' }[type] || 'text');

function PdfFormFiller({
  template,          // { id, form_name, pdf_file_path, field_map }
  athleteId,
  parentId,
  signerName,
  onSigned,
  onCancel,
}) {
  const fields = useMemo(() => {
    const raw = template?.field_map;
    if (!raw) return [];
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [template]);

  const signatureField = fields.find((f) => f.type === 'signature') || null;
  const inputFields = useMemo(() => fields.filter((f) => f.type !== 'signature'), [fields]);

  const [values, setValues] = useState({});
  const [loadingPrefill, setLoadingPrefill] = useState(true);
  const [prefilledKeys, setPrefilledKeys] = useState(new Set());
  const [printedName, setPrintedName] = useState(signerName || '');
  const [consented, setConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [focusKey, setFocusKey] = useState(null);
  // Narrow screens get the stepped list; wide ones the overlay. Users can switch.
  const [mode, setMode] = useState(() => (window.innerWidth < 820 ? 'list' : 'overlay'));
  const [listIndex, setListIndex] = useState(0);

  // PNG data URL from the pad, or '' when cleared.
  const [signatureData, setSignatureData] = useState('');

  const authToken = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('ams_user') || '{}')?.token || null;
    } catch {
      return null;
    }
  }, []);

  // ── prefill from what the gym already knows ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingPrefill(true);
        const res = await api.get(`${API_BASE_URL}/api/forms/prefill`, {
          params: { template_id: template.id, athlete_id: athleteId, parent_id: parentId || undefined },
        });
        if (cancelled) return;
        const vals = res.data?.values || {};
        setValues(vals);
        setPrefilledKeys(new Set(Object.keys(vals)));
      } catch {
        // Prefill is a convenience; the form still works fully without it.
        if (!cancelled) setPrefilledKeys(new Set());
      } finally {
        if (!cancelled) setLoadingPrefill(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.id, athleteId, parentId]);

  const setValue = useCallback((key, v) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    setError('');
  }, []);

  const missingRequired = inputFields.filter(
    (f) => f.required && !String(values[f.key] ?? '').trim()
  );

  const filledCount = inputFields.filter((f) => String(values[f.key] ?? '').trim()).length;

  // ── submit ──
  const submit = async () => {
    if (!printedName.trim()) {
      setError('Type the printed name of whoever is signing.');
      return;
    }
    if (!consented) {
      setError('Tick the consent box to sign electronically.');
      return;
    }
    if (!signatureField) {
      setError('This form has no signature field mapped. Ask an admin to map it.');
      return;
    }
    if (!signatureData) {
      setError('Draw a signature to sign.');
      return;
    }
    if (missingRequired.length) {
      setError(`Fill in: ${missingRequired.map((f) => f.label).join(', ')}`);
      // Take them to the first thing that's missing rather than just complaining.
      if (mode === 'list') setListIndex(inputFields.indexOf(missingRequired[0]));
      setFocusKey(missingRequired[0].key);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const res = await api.post(`${API_BASE_URL}/api/forms/submit`, {
        form_template_id: template.id,
        athlete_id: athleteId,
        parent_id: parentId || undefined,
        // The typed values are the form data; the server stamps them onto the PDF
        // using the same field map this screen rendered from.
        form_data: { ...values, printed_name: printedName.trim(), signed_via: 'pdf_form_filler' },
        signature_data: signatureData,
        signature_name: printedName.trim(),
        esign_consent: true,
        esign_consent_text: CONSENT_TEXT,
      // Stamping every field and uploading the signed copy to storage takes
      // longer than the shared client's default, and timing out here would look
      // to the parent like their signature failed.
      }, { timeout: 60_000 });
      onSigned?.(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the signed form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const pdfUrl = templatePdfUrl(template, API_BASE_URL);

  // ── a single field's control, shared by both layouts ──
  const renderControl = (f, { style, fontPx, autoFocus } = {}) => {
    const isPrefilled = prefilledKeys.has(f.key);
    const common = {
      className: `pff-input ${isPrefilled ? 'prefilled' : ''}`,
      style: { ...style, fontSize: fontPx ? `${fontPx}px` : undefined },
      value: values[f.key] ?? '',
      onChange: (e) => setValue(f.key, e.target.value),
      onFocus: () => setFocusKey(f.key),
      autoFocus,
      'aria-label': f.label,
    };

    if (f.type === 'choice' && Array.isArray(f.options) && f.options.length) {
      return (
        <div className="pff-choice" style={style}>
          {f.options.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`pff-choice-btn ${values[f.key] === o.value ? 'on' : ''}`}
              onClick={() => setValue(f.key, values[f.key] === o.value ? '' : o.value)}
            >
              {o.value}
            </button>
          ))}
        </div>
      );
    }
    if (f.type === 'checkbox') {
      return (
        <input
          type="checkbox"
          className="pff-checkbox"
          style={style}
          checked={!!values[f.key]}
          onChange={(e) => setValue(f.key, e.target.checked)}
          aria-label={f.label}
        />
      );
    }
    return <input type={inputTypeFor(f.type)} {...common} />;
  };

  // ── overlay layout ──
  const overlay = ({ pageIndex, cssWidth, cssHeight, pdfHeight }) => (
    <>
      {fields
        .filter((f) => (f.page || 0) === pageIndex && f.type !== 'signature')
        .map((f) => {
          // Choices get one button per option, each sitting on its OWN printed
          // word. Stretching buttons across the whole group would put "Yes"
          // under the word "No" whenever the gaps are uneven.
          if (f.type === 'choice' && Array.isArray(f.options) && f.options.length) {
            return f.options.map((o) => (
              <button
                key={`${f.key}-${o.value}`}
                type="button"
                className={`pff-optbtn ${values[f.key] === o.value ? 'on' : ''}`}
                style={rectToStyle(o.rect, cssWidth, cssHeight)}
                onClick={() => setValue(f.key, values[f.key] === o.value ? '' : o.value)}
                title={`${f.label}: ${o.value}`}
                aria-label={`${f.label}: ${o.value}`}
                aria-pressed={values[f.key] === o.value}
              />
            ));
          }
          return (
            <div
              key={f.key}
              className={`pff-slot ${focusKey === f.key ? 'focus' : ''}`}
              style={rectToStyle(f.rect, cssWidth, cssHeight)}
              title={f.label}
            >
              {renderControl(f, { fontPx: fieldFontPx(f, cssHeight, pdfHeight) })}
            </div>
          );
        })}
      {/* Show where the signature will land, without making it drawable here. */}
      {signatureField && (signatureField.page || 0) === pageIndex && (
        <div className="pff-sigmark" style={rectToStyle(signatureField.rect, cssWidth, cssHeight)}>
          {signatureData ? '✓ signed' : '✍️ sign below'}
        </div>
      )}
    </>
  );

  const current = inputFields[listIndex];

  return (
    <div className="pff">
      <div className="pff-bar">
        <div className="pff-bar-info">
          <strong>{template.form_name}</strong>
          {!loadingPrefill && (
            <span className="pff-progress">
              {filledCount}/{inputFields.length} filled
              {prefilledKeys.size > 0 && ` · ${prefilledKeys.size} from your records`}
            </span>
          )}
        </div>
        <div className="pff-bar-modes">
          <button
            type="button"
            className={mode === 'overlay' ? 'on' : ''}
            onClick={() => setMode('overlay')}
          >
            On the form
          </button>
          <button
            type="button"
            className={mode === 'list' ? 'on' : ''}
            onClick={() => setMode('list')}
          >
            One at a time
          </button>
        </div>
      </div>

      {fields.length === 0 && (
        <div className="pff-error">
          This form has not been set up for filling in yet. An admin needs to map its fields.
        </div>
      )}

      {/* ── the document ── */}
      {mode === 'overlay' ? (
        <div className="pff-doc">
          {pdfUrl && <PdfPageStack url={pdfUrl} authToken={authToken} renderOverlay={overlay} maxWidth={880} />}
        </div>
      ) : (
        <div className="pff-list">
          <div className="pff-doc pff-doc-preview">
            {pdfUrl && <PdfPageStack url={pdfUrl} authToken={authToken} renderOverlay={overlay} maxWidth={620} />}
          </div>

          {current && (
            <div className="pff-step">
              <div className="pff-step-head">
                <span className="pff-step-count">Field {listIndex + 1} of {inputFields.length}</span>
                {current.section && <span className="pff-step-section">{current.section}</span>}
              </div>
              <label className="pff-step-label" htmlFor={`pff-${current.key}`}>
                {current.label}{current.required ? ' *' : ''}
              </label>
              <div className="pff-step-control" id={`pff-${current.key}`}>
                {renderControl(current, { autoFocus: true })}
              </div>
              {prefilledKeys.has(current.key) && (
                <p className="pff-step-note">Filled from your records — change it if it’s wrong.</p>
              )}
              <div className="pff-step-nav">
                <button
                  type="button"
                  className="pff-btn-ghost"
                  disabled={listIndex === 0}
                  onClick={() => setListIndex((i) => Math.max(0, i - 1))}
                >
                  ← Back
                </button>
                <button
                  type="button"
                  className="pff-btn-ghost"
                  disabled={listIndex >= inputFields.length - 1}
                  onClick={() => setListIndex((i) => Math.min(inputFields.length - 1, i + 1))}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── sign ── */}
      <div className="pff-sign">
        <h3>Sign this form</h3>
        <label className="pff-f">
          <span>Printed name of parent / guardian *</span>
          <input value={printedName} onChange={(e) => setPrintedName(e.target.value)} placeholder="Full legal name" />
        </label>

        <div className="pff-padwrap">
          <SignaturePad onChange={setSignatureData} />
        </div>

        <label className="pff-consent">
          <input type="checkbox" checked={consented} onChange={(e) => setConsented(e.target.checked)} />
          <span>{CONSENT_TEXT}</span>
        </label>

        {error && <div className="pff-error">{error}</div>}

        <div className="pff-actions">
          {onCancel && (
            <button type="button" className="pff-btn-ghost" onClick={onCancel} disabled={submitting}>
              Cancel
            </button>
          )}
          <button type="button" className="pff-btn-primary" onClick={submit} disabled={submitting}>
            {submitting ? 'Saving…' : '✍️ Sign & save form'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PdfFormFiller;
